'use client';

// P29.9 pricing tiers. 5-column SaaS table on desktop, vertical stack on
// mobile. Cumulative bullet lists per tier; Diamond highlighted "Più popolare".
// Each CTA fires Plausible "Pricing Tier Clicked" + "CTA Click". Pulse on the
// recommended tier (skipped under useReducedMotion).

import Link from 'next/link';
import { m, useReducedMotion } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import { trackEvent } from '@/lib/analytics/plausible';

type TierKey = 'silver' | 'gold' | 'platinum' | 'diamond' | 'titan';

type TierConfig = {
  key: TierKey;
  recommended?: boolean;
  free?: boolean;
};

const TIERS: TierConfig[] = [
  { key: 'silver', free: true },
  { key: 'gold' },
  { key: 'platinum' },
  { key: 'diamond', recommended: true },
  { key: 'titan' },
];

function trackTier(tier: TierKey) {
  trackEvent('Pricing Tier Clicked', { tier });
  trackEvent('CTA Click', { variant: `pricing-tier-${tier}` });
}

export function PricingTiers() {
  const t = useTranslations('marketing.pricing');
  const locale = useLocale();
  const reduceMotion = useReducedMotion();
  const localePrefix = `/${locale}`;

  const initial = reduceMotion ? false : { opacity: 0, y: 16 };
  const whileInView = { opacity: 1, y: 0 };

  return (
    <section id="pricing" data-testid="marketing-pricing" className="bg-white py-20">
      <div className="mx-auto max-w-6xl px-4">
        <div className="text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
            {t('sectionTitle')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-600 md:text-base">
            {t('sectionSubtitle')}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-5">
          {TIERS.map((tier, idx) => {
            const features = t.raw(`tiers.${tier.key}.features`) as string[];
            const ctaLabel = tier.free ? t('ctaFree') : t('ctaTrial');
            const href = tier.free
              ? `${localePrefix}/signup`
              : `${localePrefix}/signup?plan=${tier.key}`;

            return (
              <m.article
                key={tier.key}
                data-testid={`pricing-tier-${tier.key}`}
                initial={initial}
                whileInView={whileInView}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
                whileHover={reduceMotion ? undefined : { scale: 1.02 }}
                className={`relative flex flex-col rounded-lg border bg-white p-6 ${
                  tier.recommended
                    ? 'border-emerald-600 ring-1 shadow-md ring-emerald-200'
                    : 'border-slate-200'
                }`}
              >
                {tier.recommended ? (
                  <span
                    data-testid={`pricing-tier-${tier.key}-badge`}
                    className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-700 px-3 py-0.5 text-[10px] font-semibold tracking-widest text-white uppercase"
                  >
                    {t('recommended')}
                  </span>
                ) : null}
                <h3 className="text-lg font-semibold text-slate-900">
                  {t(`tiers.${tier.key}.name`)}
                </h3>
                <p className="mt-1 min-h-[2.5rem] text-xs text-slate-500">
                  {t(`tiers.${tier.key}.tagline`)}
                </p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-3xl font-semibold text-slate-900">
                    {t(`tiers.${tier.key}.price`)}
                  </span>
                  <span className="text-xs text-slate-500">{t('perMonth')}</span>
                </div>
                <ul className="mt-5 flex-1 space-y-2 text-xs text-slate-700">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check
                        className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-700"
                        aria-hidden
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  href={href}
                  data-testid={`pricing-tier-${tier.key}-cta`}
                  onClick={() => trackTier(tier.key)}
                  className={`mt-6 inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium ${
                    tier.recommended
                      ? 'bg-emerald-700 text-white hover:bg-emerald-800'
                      : 'border border-emerald-700 text-emerald-700 hover:bg-emerald-50'
                  }`}
                >
                  {ctaLabel}
                </Link>
              </m.article>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Check className="h-3.5 w-3.5 text-emerald-700" aria-hidden /> {t('trustLine1')}
          </span>
          <span className="inline-flex items-center gap-1">
            <Check className="h-3.5 w-3.5 text-emerald-700" aria-hidden /> {t('trustLine2')}
          </span>
          <span className="inline-flex items-center gap-1">
            <Check className="h-3.5 w-3.5 text-emerald-700" aria-hidden /> {t('trustLine3')}
          </span>
        </div>
      </div>
    </section>
  );
}
